import React, { forwardRef, useImperativeHandle, useState, useRef, useCallback } from 'react';
import { 
  Box, 
  Checkbox, 
  ListItemText,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Divider
} from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface StatusFilterProps {
  api: any;
  column: any;
  columnApi: any;
  context: any;
}

const StatusFilter = forwardRef((props: StatusFilterProps, ref) => {
  const { t } = useTranslation();
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [dynamicOptions, setDynamicOptions] = useState<{ value: string, label: string }[]>([]);
  const currentFilterRef = useRef<string[]>([]);
  const statusMap: Record<string, string> = {
    "ASSIGNED": t('screens.trips.statusOptions.ASSIGNED'),
    "ALLOCATED": t("screens.trips.statusOptions.ALLOCATED"),
    "IN PROGRESS": t("screens.trips.statusOptions.IN_PROGRESS"),
    "WAITING FOR DOCUMENTATION": t("screens.trips.statusOptions.WAITING_FOR_DOCUMENTATION"),
    "COMPLETED": t("screens.trips.statusOptions.COMPLETED"),
  };

  // Update options when filter is opened or data changes
  const updateOptions = useCallback(() => {
    const rowData: any[] = [];
    props.api.forEachNodeAfterFilterAndSort((node: any) => {
      if (node.data && node.data.status) {
        rowData.push(node.data.status);
      }
    });
    const unique = Array.from(new Set(rowData));
    setDynamicOptions(unique.map(value => ({ value, label: statusMap[value] || value })));
  }, [props.api, t]);

  React.useEffect(() => {
    updateOptions();
    // eslint-disable-next-line
  }, [props.api, t, props.api.getDisplayedRowCount()]);

  useImperativeHandle(ref, () => ({
    isFilterActive() {
      return currentFilterRef.current.length > 0;
    },
    doesFilterPass(params: any) {
      if (currentFilterRef.current.length === 0) return true;
      const value = params.data.status;
      return currentFilterRef.current.includes(value);
    },
    getModel() {
      if (this.isFilterActive()) {
        return {
          type: 'set',
          values: currentFilterRef.current,
        };
      }
      return null;
    },
    setModel(model: any) {
      const newValues = model?.values || [];
      setSelectedValues(newValues);
      currentFilterRef.current = newValues;
    },
    getModelAsString() {
      if (currentFilterRef.current.length === 0) return '';
      return currentFilterRef.current
        .map(value => statusMap[value] || value)
        .join(', ');
    }
  }), [statusMap]);

  const handleCheckboxChange = (value: string) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    setSelectedValues(newValues);
    currentFilterRef.current = newValues;
    props.api.onFilterChanged();
  };

  const handleReset = () => {
    setSelectedValues([]);
    currentFilterRef.current = [];
    props.api.onFilterChanged();
  };

  return (
    <Box sx={{ 
      width: '180px',
      bgcolor: 'background.paper',
      borderRadius: 1,
      boxShadow: 1
    }}>
      <List sx={{ 
        py: 0,
        maxHeight: '200px',
        overflowY: 'auto'
      }}>
        {dynamicOptions.map((option) => (
          <ListItem key={option.value} disablePadding>
            <ListItemButton
              dense
              onClick={() => handleCheckboxChange(option.value)}
              sx={{
                minHeight: '28px',
                py: 0.5,
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: '28px' }}>
                <Checkbox
                  edge="start"
                  checked={selectedValues.includes(option.value)}
                  tabIndex={-1}
                  disableRipple
                  size="small"
                  sx={{ 
                    padding: '2px',
                    '& .MuiSvgIcon-root': {
                      fontSize: '16px'
                    }
                  }}
                />
              </ListItemIcon>
              <ListItemText 
                primary={option.label}
                primaryTypographyProps={{
                  variant: 'body2',
                  color: 'text.primary',
                  sx: {
                    fontSize: '0.75rem',
                    lineHeight: 1.2
                  }
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end',
          p: 0.5
        }}
      >
        <Button
          size="small"
          variant="outlined"
          onClick={handleReset}
          sx={{ 
            minWidth: '60px',
            textTransform: 'none',
            fontSize: '0.75rem',
            py: 0.25,
            px: 1,
            borderColor: 'divider',
            '&:hover': {
              borderColor: 'primary.main',
              backgroundColor: 'action.hover'
            }
          }}
        >
          {t('common.reset')}
        </Button>
      </Box>
    </Box>
  );
});

export default StatusFilter; 